<template>
    <div class="dashboard">
        <b-loading :is-full-page="true" :active.sync="isLoadingOrRecalculating"></b-loading>
        <div class="project-heading">
            <div class="title is-size-4 is-inline">
                <div>
                    <span class="is-vertical-center">{{ isLoading ? 'Loading Project...' : project.name }}</span>
                    <span v-if="!isLoading" class="tag is-vertical-center" :class="projectServiceHelper.getStateStyle(project.state)">{{ project.state }}</span>
                </div>
            </div>
            <div class="project-toolbar field is-grouped is-grouped-right">
                <p class="control">
                    <router-link class="button is-outlined" to="/">Back</router-link>
                </p>
                <p class="control">
                    <button v-if="$can('update', 'ProjectDashboard')" type="submit" class="button is-default" @click="openWeightAdjustmentModal()" :disabled="isSaving || isExporting">
                        Adjust Metric Weights
                    </button>
                    <button v-else type="submit" class="button is-default" @click="openWeightAdjustmentModal(true)" :disabled="isSaving || isExporting">
                        View Metric Weights
                    </button>
                </p>
                <p class="control">
                    <button type="submit" class="button is-default" @click="openProjectSettings()" :disabled="isSaving || isExporting">
                        Settings
                    </button>
                </p>
                <p class="control">
                    <button type="submit" class="button is-default" @click="exportAllSlides()" :disabled="isSaving || isExporting || !chartProject.metrics.length">
                        Export Slides
                        <i v-if="isExporting" class="fas fa-spinner fa-spin is-small"></i>
                    </button>
                </p>
                <p class="control">
                    <button class="button is-default" @click="openExcelExport()" :disabled="isSaving || !chartProject.metrics.length">
                        Export Excel
                    </button>
                </p>
                <p class="control" v-if="isProjectOwner && $can('update', 'ProjectDashboard')">
                        <button class="button is-default" @click="openProjectShareModal">Share</button>
                    </p>
                <p class="control" v-if="isProjectOwner && $can('update', 'ProjectDashboard')">
                    <button type="submit" class="button is-primary" @click="saveProject()" :disabled="isSaving">
                        {{ isSaving ? 'Saving' : 'Save' }}
                        <i v-if="isSaving" class="fas fa-spinner fa-spin is-small"></i>
                    </button>
                </p>
            </div>
            <div style="clear: both"></div>
        </div>
        <b-notification type="is-danger" v-if="errorMessage" :closable="false">
            <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
        </b-notification>
        <section ref="dashboard-settings" class="dashboard-section project-details" :class="{'sticky-settings-container':hasStickySettings}">
            <div :class="{'sticky-settings':hasStickySettings}">
                <div class="project-info-section">
                    <div class="is-inline is-pulled-left">
                        <span v-if="isLoading"> Loading project information... </span>
                        <span v-else>
                            <span> Conversation Topic: </span>
                            <span> {{project.settings.conversationTopic.name}} </span>
                            <span v-if="project.settings.description">
                                (<span :title="project.settings.description" class="conversation-topic-desc">{{project.settings.description}}</span>)
                            </span>
                        </span>
                    </div>

                    <div class="is-inline is-pulled-right">
                        <span v-if="isLoading"> Loading available dates... </span>
                        <span v-else> Data available from {{availableMetricDateRange.startDate | formatDate}} to {{availableMetricDateRange.endDate | formatDate}} </span>
                    </div>
                </div>

                <div class="control">
                    <div class="is-pulled-left">
                        <b-field grouped>
                            <b-field horizontal label="Trend Period" position="is-centered">
                                <b-select v-model="selectedPeriod">
                                    <option v-for="period in availablePeriods" :value="period.key" :key="period.key">
                                        {{ period.name }}
                                    </option>
                                </b-select>
                            </b-field>
                            <b-field horizontal label="Trend Smoothing" :class="isDayPeriodTypeSelected ? '' : 'light-text'" position="is-centered">
                                <b-numberinput v-model="daySmoothing.value" :min="daySmoothing.min" :max="daySmoothing.max" :disabled="!isDayPeriodTypeSelected"></b-numberinput>
                            </b-field>
                        </b-field>
                    </div>
                    <div class="is-pulled-right">
                        <b-field>
                            <b-button @click="openDateRangeSelection" type="is-primary" outlined>
                                {{selectedDateRange.startDate | customDateFormat('DD MMM YYYY')}} -
                                {{selectedDateRange.endDate | customDateFormat('DD MMM YYYY')}}
                            </b-button>
                        </b-field>
                    </div>
                </div>
            </div>
        </section>
        <section class="dashboard-section project-charts">
            <div class="tile is-ancestor is-vertical">

                <div class="tile is-parent is-12">
                    <div class="tile is-child box element">
                        <final-score-table
                            :project="chartProject"
                            v-on:on-chart-mount-complete="registerChartForExport"/>
                    </div>
                </div>

                <div class="tile">
                    <div class="tile is-parent is-6">
                        <div class="tile is-child box element">
                            <final-score-swot-chart
                                :project="chartProject"
                                v-on:on-chart-mount-complete="registerChartForExport"/>
                        </div>
                    </div>
                    <div class="tile is-parent is-6">
                        <div class="tile is-child box element">
                            <final-score-progress-chart
                                :project="chartProject"
                                v-on:on-chart-mount-complete="registerChartForExport"/>
                        </div>
                    </div>
                </div>

                <div class="tile">
                    <div class="tile is-parent is-6">
                        <div class="tile is-child box element">
                            <brand-pillar-comparison-chart
                                :project="chartProject"
                                v-on:on-chart-mount-complete="registerChartForExport"/>
                        </div>
                    </div>
                    <div class="tile is-parent is-6">
                        <div class="tile is-child box element">
                            <socialnetworks-comparison-chart :project="chartProject"
                                v-on:on-chart-mount-complete="registerChartForExport"/>
                        </div>
                    </div>
                </div>

                <div class="tile">
                    <div class="tile is-parent is-6">
                        <div class="tile is-child box element">
                            <brand-pillar-detail-chart
                                :project="chartProject"
                                v-on:on-chart-mount-complete="registerChartForExport"/>
                        </div>
                    </div>
                    <div class="tile is-parent is-6">
                        <div class="tile is-child box element">
                            <socialnetworks-progress-chart
                                :project="chartProject"
                                v-on:on-chart-mount-complete="registerChartForExport"/>
                        </div>
                    </div>
                </div>

                <div class="tile">
                    <div class="tile is-parent is-6">
                        <div class="tile is-child box element">
                            <competitive-metric-chart
                                :project="chartProject"
                                v-on:on-chart-mount-complete="registerChartForExport"/>
                        </div>
                    </div>
                    <div class="tile is-parent is-6">
                        <div class="tile is-child box element">
                            <brand-metric-detail-progress-chart
                                :project="chartProject"
                                v-on:on-chart-mount-complete="registerChartForExport"/>
                        </div>
                    </div>
                </div>

            </div>
        </section>

    </div>
</template>

<style lang="scss">
.dashboard {
    .dashboard-section {
        padding: 1.5rem;

        .conversation-topic-desc {
            max-width: 350px;
            text-overflow: ellipsis;
            overflow-x: hidden;
            white-space: nowrap;
            display: table-cell;
        }
    }
    .project-heading {
        & > .title {
            float: left;
            margin: 0;
            padding-top:0.2rem;
        }

        padding-left: 1.5rem;
        padding-right: 1.5rem;
        padding-top: 0;
        padding-bottom: 0;

        .project-toolbar {
            float: right;
            margin-bottom: 0;
            p.control {
                padding-bottom: 0;
            }
            button > i {
                margin-left: 0.5rem;
            }
        }

        .available-date-display {
            font-size: 0.8rem;
            padding-top: 0.5em;
        }

        .is-vertical-center {
            vertical-align: middle;
            padding-right: 0.5em;
        }
    }

    .project-info-section {
        width:100%;
        padding-top:0;
        font-size: 0.8rem;
        font-weight: 600;
        color:$dark;

        div {
            padding-bottom: 0.5rem;
        }
    }

    article.notification {
        i {
            margin-right: 0.5rem;
        }
    }

    .light-text {
        .label {
            color: $text-light
        }
    }

    .project-details {
        width:100%;
        margin:0;
        padding-top:1rem;
        padding-bottom:1rem;
        overflow: hidden;

        .tile.is-parent {
            flex-wrap: wrap;
        }

        .element {
            padding: 10px !important;
            margin: 5px !important;
        }

        p {
            display: flex;
            height: 2rem;

            span.is-spaced-out{
                margin:0 1rem;
            }
        }

        .field-label {
            flex-basis: auto;
            margin-right: 1rem;
        }
    }

    .project-charts {
        padding-top: 0rem;

        button i {
            margin-left:0.25rem;
        }
    }

    .sticky-settings {
        background: $panel-background;
        position: fixed;
        top: 0;
        left:0;
        width: 100%;
        z-index: 39; // this is just below the z-index used by the modal
        padding-top: 1rem;
        padding-bottom: 0.5rem;
        padding-left: 2.25rem;
        padding-right: 2.25rem;
        border-bottom: solid 1px $border;
    }

    /* Add some top padding to the page content to prevent sudden quick movement (as the header gets a new position at the top of the page (position:fixed and top:0) */
    .sticky-settings-container {
        padding-top: 5.8rem;
    }
}
</style>

<script>
import _ from 'lodash';

import projectService from '@/services/project-service.js';
import { metricService } from '@/services/metric-service.js';
import { scoreService } from '@/services/score-service';
import defaultMetricWeightService from '@/services/default-metric-weight-service.js';
import colourService from '@/services/colour-service';
import { exporterService } from '@/services/exporter-service';
import { dateHelperService } from '@/services/date-helper-service';
import { dateRangeSelectionService } from '@/services/date-range-selection-service';
import { projectServiceHelper } from '@/services/project-service-helper.js';
import { infoSlideRequestor } from '@/requestors/info-slide-requestor.js';
import { cacheService } from '@/services/cache-service.js';

import ProjectWeightAdjustment from '@/components/ProjectWeightAdjustment.vue';
import ProjectSettings from '@/components/ProjectSettings.vue';
import DateRangeSelection from '@/components/DateRangeSelection.vue';
import FinalScoreTable from '@/components/FinalScoreTable.vue';
import FinalScoreSwotChart from '@/components/FinalScoreSwotChart.vue';
import FinalScoreProgressChart from '@/components/FinalScoreProgressChart.vue';
import BrandPillarComparisonChart from '@/components/BrandPillarComparisonChart.vue';
import BrandPillarDetailChart from '@/components/BrandPillarDetailChart.vue';
import SocialnetworksComparisonChart from '@/components/SocialnetworksComparisonChart.vue';
import SocialnetworksProgressChart from '@/components/SocialnetworksProgressChart.vue';
import CompetitiveMetricChart from '@/components/CompetitiveMetricChart.vue';
import BrandMetricDetailProgressChart from '@/components/BrandMetricDetailProgressChart.vue';
import ProjectShare from '@/components/ProjectShare.vue';
import ExportExcelReport from '@/components/ExportExcelReport.vue';

import constants from '@/constants.js';

export default {
    name: 'ProjectDashboard',
    components: {
        FinalScoreTable,
        FinalScoreSwotChart,
        FinalScoreProgressChart,
        BrandPillarComparisonChart,
        BrandPillarDetailChart,
        SocialnetworksComparisonChart,
        SocialnetworksProgressChart,
        CompetitiveMetricChart,
        BrandMetricDetailProgressChart
    },
    props: {
        projectId: {
            type: Number,
            required: true
        }
    },
    data () {
        return {
            projectServiceHelper,
            errorMessage: '',
            isLoading: true,
            isRecalculating: false,
            isSaving: false,
            metrics: [],
            rawMetrics: [],
            project: {},
            daySmoothing: {
                min: 1,
                max: 1,
                value: 1
            },
            availableMetricDateRange: {
                isEmpty: true,
                dates: []
            },
            defaultMetricWeights: [],
            defaultPillarWeights: [],
            originalContent: {},
            isExporting: false,
            registeredChartList: [],
            hasStickySettings: false,
            dasboardSettingsOffsetTop: 0,
            availablePeriods: [
                { key: constants.PERIOD.DAY, name: 'Day' },
                { key: constants.PERIOD.WEEK, name: 'Week' },
                { key: constants.PERIOD.MONTH, name: 'Month' },
                { key: constants.PERIOD.QUARTER, name: 'Quarter' },
                { key: constants.PERIOD.YEAR, name: 'Year' }
            ],
            selectedPeriod: constants.PERIOD.DAY,
            chartProject: {
                id: this.projectId,
                settings: {},
                metrics: [],
                utcDateRange: { start: undefined, end: undefined },
                meta: {}
            },
            // This date range is used to bind to the datepicker ONLY.
            // For all other purposes involving calulations etc, use utcDateRange
            selectedDateRange: {}
        };
    },
    mounted () {
        if (this.$refs['dashboard-settings']) {
            this.dasboardSettingsOffsetTop = this.$refs['dashboard-settings'].offsetTop;
        }
    },
    async created () {
        try {
            const results = await Promise.all([
                projectService.getProject(this.projectId),
                metricService.getAvailableMetricDateRangeForProject(this.projectId),
                defaultMetricWeightService.getDefaultMetricWeights(),
                defaultMetricWeightService.getDefaultPillarWeights()
            ]);
            const [project, availableMetrics, defaultMetricWeights, defaultPillarWeights] = results;
            if (project) {
                this.project = project.data;
                if (this.project.state === constants.PROJECT_STATE.DRAFT) {
                    this.$router.push('/');
                }
                this.project.settings = JSON.parse(this.project.projectJSON);

                // temporary hack to support old format wich uses indexes instead of unique ids
                this.project.settings && this.project.settings.columns.forEach(c => { c.id = c.id.toString(); });

                delete this.project.projectJSON;
                setColoursToColumns(this.project.settings.columns);
                setEnabilityToColumns(this.project.settings.columns);
            }
            if (defaultMetricWeights) {
                this.defaultMetricWeights = defaultMetricWeights.data;
            }
            if (defaultPillarWeights) {
                this.defaultPillarWeights = defaultPillarWeights.data;
            }
            if (availableMetrics) {
                // d.date is already in ISO format so will be converted to correct UTC Date
                let availableDates = _.forEach(availableMetrics.data, d => { d.date = new Date(d.date); });
                this.availableMetricDateRange.dates = availableDates.reduce((acc, cur) => {
                    cur.isMetricComplete && acc.push({ date: cur.date, type: 'is-info' });
                    return acc;
                }, []);
                if (availableDates.length === 0) {
                    this.availableMetricDateRange.isEmpty = true;
                    this.availableMetricDateRange.startDate = new Date();
                    this.availableMetricDateRange.endDate = new Date();
                } else {
                    this.availableMetricDateRange.isEmpty = false;
                    this.availableMetricDateRange.startDate = _.minBy(availableDates, d => d.date).date;
                    this.availableMetricDateRange.endDate = _.maxBy(availableDates, d => d.date).date;
                }

                this.daySmoothing.max = numberOfDaysBetween(this.availableMetricDateRange.start, this.availableMetricDateRange.end);
                let dateQuickSelections = dateRangeSelectionService.buildDateQuickSelections(this.availableMetricDateRange.startDate, this.availableMetricDateRange.endDate);
                let defaultDateRange = dateRangeSelectionService.getDefaultQuickSelection(dateQuickSelections);
                // As 'selectedDateRange' property has a watch and when we assign the defaultDateRange value to the selectedDateRange
                // property, the watch will trigger and the loadMetrics function will be called. Therefore we do not need to
                // call the loadMetrics function in this created function.
                this.selectedDateRange = defaultDateRange;
            }
            this.setOriginalProjectObject();
        } catch (error) {
            this.errorMessage = error.message;
        } finally {
            this.isLoading = false;
        }
        // Subscribe to windows beforeunload to check for unsaved changes
        // Use a method defined below to ensure correct scope is accessable
        window.addEventListener('beforeunload', this.warnOnWindowChange);
        window.addEventListener('scroll', this.setStickySettings);
        window.addEventListener('resize', this.onWindowResize);
    },
    methods: {
        warnOnWindowChange (event) {
            if (this.hasChanges()) {
                event.preventDefault();
                event.returnValue = true;
            }
        },
        setStickySettings () {
            this.hasStickySettings = window.pageYOffset > this.dasboardSettingsOffsetTop;
        },
        onWindowResize () {
            if (this.$refs['dashboard-settings']) {
                this.dasboardSettingsOffsetTop = this.$refs['dashboard-settings'].offsetTop;
            }
        },
        async saveProject () {
            this.isSaving = true;
            try {
                const content = this.serializedContent();
                await projectService.updatePublishedProject(content);
                this.$buefy.toast.open({
                    message: this.project.name + ' has been saved',
                    type: 'is-success'
                });
                this.setOriginalProjectObject();
            } catch (error) {
                if (error.response && error.response.data && error.response.data.message) {
                    this.errorMessage = error.response.data.message;
                } else {
                    this.errorMessage = error.message;
                }
            } finally {
                this.isSaving = false;
            }
        },
        openWeightAdjustmentModal (isReadOnly) {
            let modalConfig = {
                parent: this,
                component: ProjectWeightAdjustment,
                hasModalCard: true,
                props: {
                    inputMetricWeights: this.project.settings.metricWeights,
                    inputPillarWeights: this.project.settings.pillarWeights,
                    isReadOnly: isReadOnly
                },
                events: {
                    projectWeightsChanged: async (metricWeights, pillarWeights) => {
                        this.project.settings.metricWeights = this.areWeightsEquivalentToDefault(metricWeights, this.defaultMetricWeights)
                            ? []
                            : metricWeights;
                        this.project.settings.pillarWeights = this.areWeightsEquivalentToDefault(pillarWeights, this.defaultPillarWeights)
                            ? []
                            : pillarWeights;
                        await this.calculateAverageMetrics();
                        await this.calculateMetrics();
                    }
                }
            };
            this.$buefy.modal.open(modalConfig);
        },
        openProjectSettings () {
            let modalConfig = {
                parent: this,
                component: ProjectSettings,
                hasModalCard: true,
                props: {
                    project: this.project
                },
                events: {

                    projectSaved: (config) => {
                        this.project.settings.config = Object.assign({}, this.project.settings.config, config);
                        this.calculateMetrics();
                    }
                }
            };
            this.$buefy.modal.open(modalConfig);
        },
        openDateRangeSelection () {
            let modalConfig = {
                parent: this,
                component: DateRangeSelection,
                hasModalCard: true,
                props: {
                    dates: this.availableMetricDateRange.dates,
                    selectedDateRange: this.selectedDateRange
                },
                events: {
                    dateRangeSelectionApplied: (selectedDateRange) => {
                        this.selectedDateRange = selectedDateRange;
                    }
                }
            };
            this.$buefy.modal.open(modalConfig);
        },
        openExcelExport () {
            let selectedPeriodObject = _.find(this.availablePeriods, p => p.key === this.selectedPeriod);
            let excelProject = {
                name: this.project.name,
                createdBy: this.project.createdBy,
                conversationTopic: this.project.settings.conversationTopic.name,
                conversationTheme: this.project.settings.conversationTheme && this.project.settings.conversationTheme.name
                    ? this.project.settings.conversationTheme.name
                    : undefined,
                description: this.project.settings.description,
                metricWeights: this.project.settings.metricWeights || this.defaultMetricWeights,
                pillarWeights: this.project.settings.pillarWeights || this.defaultPillarWeights,
                metrics: this.metrics,
                averageMetricsForEntireRange: this.averageMetricsForEntireRange,
                period: selectedPeriodObject,
                daySmoothing: this.daySmoothing.value
            };
            let modalConfig = {
                parent: this,
                component: ExportExcelReport,
                hasModalCard: true,
                props: {
                    project: excelProject
                },
                events: {
                }
            };
            this.$buefy.modal.open(modalConfig);
        },
        setOriginalProjectObject () {
            this.originalContent = this.serializedContent();
        },
        serializedContent () {
            const content = _.cloneDeep(this.project);
            if (this.areWeightsEquivalentToDefault(this.project.settings.metricWeights, this.defaultMetricWeights)) {
                content.settings.metricWeights = undefined;
            }
            if (this.areWeightsEquivalentToDefault(this.project.settings.pillarWeights, this.defaultPillarWeights)) {
                content.settings.pillarWeights = undefined;
            }
            // Restore properties from originalContent that you want to ignore for saving
            // for exemple, brand names in columns
            // ----------------------------------------------------
            content.projectJSON = JSON.stringify(content.settings);
            return _.pick(content, ['id', 'name', 'state', 'projectJSON', 'endDate']);
        },
        hasChanges () {
            if (this.$ability.can('update', 'ProjectDashboard') && this.isProjectOwner) {
                const currentContent = this.serializedContent();
                return !_.isEqual(this.originalContent, currentContent) &&
                    this.project.state === constants.PROJECT_STATE.PUBLISHED;
            }
        },
        confirmLoseChange () {
            if (this.hasChanges()) {
                return window.confirm('You have unsaved changes. Are you sure you want to leave this page?');
            }
            return true;
        },
        areWeightsEquivalentToDefault (weights, defaultWeights) {
            // We consider the input weights to be equivalent when one of the following conditions is met
            // 1. The input weights are undefined (so any components should just use the defaults instead)
            // 2. The input weights array has a length of zero (as per the previous step)
            // 3. The input weights match the default weights exactly, so it doesn't matter which we use.
            let isMatch =
                !weights ||
                weights.length === 0 ||
                JSON.stringify(weights) === JSON.stringify(defaultWeights);
            return isMatch;
        },
        setChartProjectData () {
            let selectedPeriodObject = _.find(this.availablePeriods, p => p.key === this.selectedPeriod);
            this.chartProject.settings = this.project.settings;
            this.chartProject.metrics = this.metrics;
            this.chartProject.averageMetricsForEntireRange = this.averageMetricsForEntireRange;
            this.chartProject.utcDateRange = this.utcDateRange;
            this.chartProject.meta.period = selectedPeriodObject;
            this.chartProject.meta.daySmoothing = this.daySmoothing.value;
        },
        async calculateMetrics () {
            this.isRecalculating = true;
            this.metrics = await scoreService.calculateScores(
                {
                    id: this.projectId,
                    columns: this.project.settings.columns,
                    metricWeights: this.project.settings.metricWeights,
                    pillarWeights: this.project.settings.pillarWeights,
                    startDate: this.utcDateRange.start,
                    endDate: this.utcDateRange.end
                },
                this.rawMetrics,
                this.selectedPeriod,
                this.daySmoothing.value,
                this.project.settings.config ? this.project.settings.config.firstDayWeek : undefined
            );
            this.setChartProjectData();
            this.isRecalculating = false;
        },
        async calculateAverageMetrics () {
            this.isRecalculating = true;
            // Calculate the average metrics for the entrie selected date range regardless of period or day smoothing selection.
            // We can acheive this by passing selectedPeriod as DAY and daySmoothing value as the total number of days between the start and end date.
            this.averageMetricsForEntireRange = await scoreService.calculateScores(
                {
                    id: this.projectId,
                    columns: this.project.settings.columns,
                    metricWeights: this.project.settings.metricWeights,
                    pillarWeights: this.project.settings.pillarWeights,
                    startDate: this.utcDateRange.start,
                    endDate: this.utcDateRange.end
                },
                this.rawMetrics,
                constants.PERIOD.DAY,
                this.daySmoothing.max, // max value is already calculated, which is the total number of days between the start and end date.
                this.project.settings.config ? this.project.settings.config.firstDayWeek : undefined
            );
            this.isRecalculating = false;
        },
        async loadMetrics () {
            this.isLoading = true;
            let hash = cacheService.getHash(this.project.id, this.utcDateRange.start, this.utcDateRange.end);
            if (cacheService.hasKey(hash)) {
                this.rawMetrics = cacheService.getCache(hash);
            } else {
                let result = await metricService.getMetricsForDateRange(this.project.id, this.utcDateRange.start, this.utcDateRange.end);
                this.rawMetrics = result.data;
                cacheService.setCache(hash, this.rawMetrics);
            }
            await this.calculateAverageMetrics();
            await this.calculateMetrics();
            this.isLoading = false;
        },
        registerChartForExport (chartDetail) {
            this.registeredChartList.push(chartDetail);
        },
        async exportAllSlides () {
            this.isExporting = true;

            // NB: Currently the slide requests will be generated in the order which they
            //     were registered. If we want to sort or garauntee uniqueness we will need
            //     a key to identify them.
            let slideRequests = [];

            // Add an info slide
            let brands = this.project.settings.columns.map((c) => {
                return {
                    name: c.brandTopic.name,
                    colour: c.colour,
                    theme: c.brandTheme && c.brandTheme.name ? c.brandTheme.name : ''
                };
            });
            let infoSlideData = {
                projectName: this.project.name,
                projectCreatedBy: this.project.createdBy,
                projectStartDate: dateHelperService.customFormat(this.chartProject.utcDateRange.start, 'DD MMM YYYY'),
                projectEndDate: dateHelperService.customFormat(this.chartProject.utcDateRange.end, 'DD MMM YYYY'),
                projectPeriod: this.chartProject.meta.period.name,
                projectConversationTopic: this.project.settings.conversationTopic.name,
                projectConversationTopicDesc: this.project.settings.description,
                projectConversationTheme: this.project.settings.conversationTheme && this.project.settings.conversationTheme.name
                    ? this.project.settings.conversationTheme.name
                    : '',
                projectBrands: brands
            };
            slideRequests.push(infoSlideRequestor.getSlideRequest(infoSlideData));

            this.registeredChartList
                .forEach(c => slideRequests.push(c.getSlideRequest()));
            let pptxRequest = {
                ResponseFileName: 'conversablity-project.pptx',
                SlideRequests: slideRequests
            };

            try {
                await exporterService.exportPPTX(pptxRequest);
            } catch (error) {
                this.$buefy.toast.open({
                    message: 'Failed to export slides for all charts',
                    type: 'is-danger'
                });
            } finally {
                this.isExporting = false;
            }
        },
        openProjectShareModal () {
            let modalConfig = {
                parent: this,
                component: ProjectShare,
                hasModalCard: true,
                props: {
                    project: this.project
                }
            };

            this.$buefy.modal.open(modalConfig);
        }
    },
    computed: {
        // buefy datepicker only returns local date/time (time is fixed at 00:00:00). As we want users to select UTC date (and time doesn't matter),
        // we will only keep the date value and convert it to a UTC datetime so with the same date value
        utcDateRange () {
            return {
                start: dateHelperService.convertToUTC(this.selectedDateRange.startDate),
                end: dateHelperService.convertToUTC(this.selectedDateRange.endDate)
            };
        },
        isProjectOwner () {
            let userId = localStorage.getItem('userId');
            let projectUserId = this.project.userId ? this.project.userId.toString() : '';
            return userId === projectUserId;
        },
        isDayPeriodTypeSelected () {
            return this.selectedPeriod === constants.PERIOD.DAY;
        },
        isArchived () {
            return this.project.state === constants.PROJECT_STATE.ARCHIVED;
        },
        isLoadingOrRecalculating () {
            return this.isLoading || this.isRecalculating;
        }
    },
    watch: {
        selectedDateRange: {
            handler: function () {
                this.daySmoothing.max = numberOfDaysBetween(this.utcDateRange.start, this.utcDateRange.end);
                if (this.daySmoothing.value >= this.daySmoothing.max) {
                    this.daySmoothing.value = this.daySmoothing.max;
                }
                this.loadMetrics();
            },
            deep: true
        },
        'daySmoothing.value': function (newPeriod, oldPeriod) {
            this.calculateMetrics();
        },
        'selectedPeriod': function (newValue, oldValue) {
            this.calculateMetrics();
        }
    },
    beforeRouteLeave (to, from, next) {
        // Check for unsaved changes and confirm whether to proceed without saving
        if (!this.confirmLoseChange()) {
            next(false);
        } else {
            next();
        }
    },
    beforeDestroy () {
        // Unsubscribe from the 'beforeunload' event correctly
        window.removeEventListener('beforeunload', this.warnOnWindowChange);
        window.removeEventListener('scroll', this.setStickySettings);
    }
};

const setColoursToColumns = (columns) => {
    // if a column doesn't have a colour specified then set it here
    let defaultColours = colourService.getColours;
    columns.forEach((col, index) => { if (!col.colour) col.colour = defaultColours[index]; });
};

const setEnabilityToColumns = (columns) => {
    columns.forEach(col => { col.enabled = true; });
};

const numberOfDaysBetween = (startDate, endDate) => {
    return Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
};
</script>
